import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { gesamttext } from './markierung.js';

/**
 * Erlaubt ist, was Markdown aus einem Ratgeber tatsaechlich erzeugt -- nicht mehr. `marked`
 * reicht rohes HTML unveraendert durch, und DOMPurifys Standardprofil liesse davon unter
 * anderem `<form>`, `<input>`, `<button>` und `style`-Attribute passieren: genug, um auf der
 * Freigabeseite eine taeuschende Oberflaeche zu malen (ein Eingabefeld, das nach unserem
 * aussieht; ein Formular, das anderswohin sendet). Deshalb eine ALLOWLIST statt einer
 * Verbotsliste -- eine Verbotsliste muesste jedes gefaehrliche Element einzeln kennen, und
 * genau das ging bei der Vorschau-Bereinigung am 15.09. schief (`vorschau-html.ts`): Dort
 * tauchten nacheinander `<a href>`, `<area href>`, SVG-`xlink:href` und SMIL-Animationen als
 * uebersehenes Schlupfloch auf, bis auf „was ist erlaubt" umgestellt wurde. Eine Allowlist
 * kennt die uebrigen Faelle gar nicht erst.
 */
const ERLAUBTE_TAGS = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'br',
  'hr',
  'strong',
  'em',
  'b',
  'i',
  'ul',
  'ol',
  'li',
  'blockquote',
  'code',
  'pre',
  'a',
  'img',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
];

/**
 * Je Tag nur die Attribute, die dafuer einen Sinn ergeben -- sonst keine, insbesondere kein
 * `style`.
 *
 * `a` behaelt `href` wieder (Ruecknahme vom 16.09.2026, dritte Rueckmeldung): Diese Seite
 * existiert, damit ein MENSCH den Ratgeber abnimmt, und die Verlinkung gehoert zu dem, was er
 * dabei beurteilt -- anders als bei `vorschau-html.ts`, wo fertiges Fremd-HTML ungeprueft
 * durchlaeuft. Ein unsichtbares Linkziel liesse den Reviewer eine falsche Produktverlinkung
 * durchwinken, ohne dass ihm etwas auffaellt. `istErlaubterVerweis` (unten) laesst dafuer nur
 * `http:`/`https:` sowie site-relative Pfade durch, kein `javascript:`, kein `data:`.
 */
const ERLAUBTE_ATTRIBUTE_JE_TAG: Record<string, string[]> = {
  a: ['href', 'title'],
  img: ['src', 'alt', 'title'],
};

/**
 * Entscheidung zu relativen Verweisen: Ratgeber-Texte verlinken untereinander, z. B.
 * `[Hagelschaden](/ratgeber/hagelschaden-am-auto)`. Ein still verschwindendes Ziel waere hier
 * besonders unauffaellig -- der Reviewer saehe nur normalen Fliesstext, nicht dass dort
 * einmal ein Verweis stand. Ein relativer Pfad loest gegen den eigenen Origin der Anwendung
 * auf und bleibt damit im selben Vertrauensbereich wie die Anwendung selbst, deshalb kommt er
 * durch. Ein absoluter `http:`/`https:`-Verweis darf auf JEDE externe Quelle zeigen -- der
 * Mensch sieht das sichtbare Ziel (siehe `ergaenzeVerweisAttribute`) und beurteilt es selbst,
 * anders als bei der automatisch geladenen Vorschau ist hier kein Skript die Kontrollinstanz.
 *
 * Die Gefahr, vor der `istErlaubterVerweis` konkret schuetzt: ein PROTOKOLL-relativer Verweis
 * ("//boese.example/x") hat ebenfalls kein eigenes Schema, meint aber einen voellig anderen
 * Host, sobald der Browser ihn aufloest. `new URL(wert, eigenerOrigin)` loest beides gleich
 * auf -- nur wenn der aufgeloeste Origin dem eigenen entspricht, ist der Verweis tatsaechlich
 * relativ.
 *
 * EINE Ausnahme bei den sonst beliebigen externen Zielen (Befund des CI-Reviews vom
 * 16.09.2026, vierte Runde): Architekturregel 1 gilt nicht nur fuer automatisches Laden,
 * sondern auch fuer einen bewussten Klick -- der Browser darf nie mit unserer eigenen
 * n8n-Instanz sprechen. Das ist keine Verbotsliste vieler moeglicher Ziele, sondern der
 * Ausschluss EINER bekannten, eigenen Adresse -- symmetrisch zu `BILD_HOSTS` oben, nur als
 * Ausschluss statt als Positivliste, weil hier (anders als bei Bildern) fast jedes Ziel
 * legitim sein kann und nur dieses eine es nicht sein darf.
 *
 * Der Hostname wird vor dem Vergleich kanonisiert (Befund des CI-Reviews vom 16.09.2026,
 * fuenfte Runde): Ein abschliessender Punkt ("n8n.cloud.") bezeichnet nur die DNS-Wurzel --
 * der Browser loest ihn gegen denselben Host auf, `url.hostname` liefert ihn aber als
 * literal andere Zeichenkette. Ohne diese Normalisierung waere der Ausschluss durch genau
 * diesen Punkt umgehbar gewesen.
 *
 * Bewusst NICHT abgedeckt (CI-Review PR #44, 2026-09-26, als Grenze eingeordnet): Ein
 * erlaubter externer Host koennte per HTTP-Redirect auf n8n weiterleiten. Das Redirect-Ziel
 * ist im Browser vor dem Klick nicht feststellbar. Vollstaendig verhindern liesse es sich nur,
 * wenn externe Verweise ganz verboten wuerden, und die gehoeren legitim in den Ratgeber. Der
 * Ausschluss oben schuetzt vor dem direkten Verweis; der Umweg ueber einen Redirector ist ein
 * akzeptiertes Restrisiko. Der Code ist in B1 nur aus `ratgeber-text.tsx` verschoben.
 */
const N8N_HOST_ENDUNG = '.n8n.cloud';

function kanonischerHostname(hostname: string): string {
  // `URL` liefert den Hostnamen zwar bereits klein geschrieben und IDNA-normalisiert, aber
  // ein abschliessender Punkt bleibt stehen -- der zaehlt zwar fuer die DNS-Aufloesung nicht
  // mit, wohl aber fuer einen stumpfen String-Vergleich. `toLowerCase()` zusaetzlich, damit
  // sich dieser Vergleich nicht auf ein Detail der URL-Implementierung verlassen muss.
  return hostname.toLowerCase().replace(/\.+$/, '');
}

function istErlaubterVerweis(wert: string): boolean {
  try {
    if (/^https?:\/\//i.test(wert)) {
      const host = kanonischerHostname(new URL(wert).hostname);
      return host !== N8N_HOST_ENDUNG.slice(1) && !host.endsWith(N8N_HOST_ENDUNG);
    }
    return new URL(wert, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Erlaubte Bildquellen -- dieselbe Liste wie `BILD_HOSTS` in `apps/backend/src/vorschau-html.ts`
 * (eigenes Workspace, deshalb dupliziert statt importiert). Ein `<img src>` laedt anders als
 * ein `<a href>` automatisch, ganz ohne Klick. Ohne diese Grenze koennte ein Sprachmodell
 * damit einen Tracking-Pixel ausloesen oder -- schlimmer -- eine Anfrage direkt an n8n, sobald
 * der Browser die Freigabeseite rendert (Architekturregel 1: der Browser darf nicht mit n8n
 * sprechen). Anders als bei der Vorschau gibt es hier keine sandboxte, CSP-bewehrte
 * Zwischenschicht -- die Seite rendert live in der echten Anwendung --, die Allowlist ist
 * also die einzige Verteidigungslinie.
 */
// Die ERGO-Hosts tragen die Bilder im Originaltext einer Produktseite (Entscheidungsseite) --
// ERGOs eigene Auslieferung, kein Tracking-Dritter und kein n8n.
const BILD_HOSTS = [
  'drive.google.com',
  'lh3.googleusercontent.com',
  'assets.ergo.com',
  'www.ergo.de',
];

/**
 * Exportiert, weil `freigabe-page.tsx` (`BildKarte`) dieselbe Grenze fuer `TextAntwort.bilder`
 * braucht: Auch dort landet ein `bild.url` aus derselben n8n-Antwort direkt in `img.src`, mit
 * derselben Gefahr eines automatischen, ungeprueften Aufrufs (Architekturregel 1). Gleicher
 * Workspace wie hier -- deshalb Export statt Dopplung (Codex-Gate-Befund MITTEL, dritte Runde).
 */
export function istErlaubteBildquelle(wert: string): boolean {
  if (wert.startsWith('data:image/')) return true;
  try {
    const url = new URL(wert);
    return url.protocol === 'https:' && BILD_HOSTS.includes(url.hostname);
  } catch {
    // Relative Adressen loesen gegen den Origin der Anwendung auf -- unser eigener, aber
    // eine Bildquelle hat dort nichts zu suchen.
    return false;
  }
}

function begrenzeAttribute(
  node: Element,
  daten: { attrName: string; attrValue: string; keepAttr: boolean },
): void {
  const tag = node.tagName.toLowerCase();
  const erlaubt = ERLAUBTE_ATTRIBUTE_JE_TAG[tag] ?? [];
  if (!erlaubt.includes(daten.attrName)) {
    daten.keepAttr = false;
    return;
  }
  if (tag === 'img' && daten.attrName === 'src' && !istErlaubteBildquelle(daten.attrValue)) {
    daten.keepAttr = false;
  }
  if (tag === 'a' && daten.attrName === 'href' && !istErlaubterVerweis(daten.attrValue)) {
    daten.keepAttr = false;
  }
}

/**
 * Ein Klick oeffnet ein NEUES Tab, statt die Freigabeseite zu verlassen -- dort kann eine noch
 * ungespeicherte Bearbeitung stehen. `rel="noopener noreferrer"` verhindert zusaetzlich, dass
 * das neue Tab per `window.opener` auf diese Seite zugreifen kann (reflexartiger Schutz beim
 * Oeffnen von Fremdinhalt in einem neuen Kontext, unabhaengig vom Linkziel). DOMPurify setzt
 * das nicht von sich aus -- deshalb ein eigener `afterSanitizeAttributes`-Hook, der NACH der
 * eigentlichen Bereinigung greift: Nur ein Element, das die Pruefung oben bereits bestanden
 * hat (also noch ein `href` traegt), bekommt die beiden Attribute.
 */
function ergaenzeVerweisAttribute(node: Element): void {
  if (node.tagName.toLowerCase() !== 'a' || !node.hasAttribute('href')) return;
  node.setAttribute('target', '_blank');
  node.setAttribute('rel', 'noopener noreferrer');
}

/**
 * Der Text kommt aus einem Sprachmodell und ist damit Fremdinhalt, auch wenn er durch unsere
 * eigene Pipeline gelaufen ist. Deshalb wird jedes gerenderte Stueck bereinigt, bevor es in
 * den Baum geht -- `marked` erzeugt HTML, `DOMPurify` nimmt ihm alles, was nicht auf der
 * Allowlist steht.
 *
 * `addHook`/`removeHook` klammern den Hook um genau diesen Aufruf: `DOMPurify` ist ein
 * globaler, geteilter Singleton -- ein Hook, der dort stehen bleibt, wuerde jeden anderen
 * `DOMPurify.sanitize`-Aufruf in dieser Seite ebenfalls einschraenken, auch mit einer eigenen,
 * bewusst anderen Konfiguration (Befund des CI-Reviews vom 16.09.2026).
 */
export function alsHtml(markdown: string): string {
  DOMPurify.addHook('uponSanitizeAttribute', begrenzeAttribute);
  DOMPurify.addHook('afterSanitizeAttributes', ergaenzeVerweisAttribute);
  try {
    return DOMPurify.sanitize(marked.parse(markdown, { async: false }) as string, {
      ALLOWED_TAGS: ERLAUBTE_TAGS,
      ALLOWED_ATTR: ['href', 'title', 'src', 'alt'],
    });
  } finally {
    DOMPurify.removeHook('uponSanitizeAttribute', begrenzeAttribute);
    DOMPurify.removeHook('afterSanitizeAttributes', ergaenzeVerweisAttribute);
  }
}

export function textVonHtml(html: string): string {
  const element = document.createElement('div');
  element.innerHTML = html;
  return gesamttext(element);
}
